import React, { useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  Button,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
} from 'reactstrap'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { Calendar } from 'react-feather'
import { isAfter } from 'date-fns'
import { enUS, ptBR } from 'date-fns/locale'

import { Divider, Wrapper, FilterContainer } from './styles'
import { brazilianStates } from '~/constants/location'
import MultiSelect from '~/components/MultiSelect'
import {
  getFilteredAreaOptionsWithStateAndCity,
  getFilteredCityOptionsWithStateAndArea,
  getFilteredStateOptionsWithCityAndArea,
} from '~/helpers/getFilteredOptions'
import { useFormFilters } from '~/hooks/useFormFilters'
import { useSearchParamsHandler } from '~/contexts/FilterContext'
import { getDefaultFiltersCount } from '~/helpers/getFilterCount'
import DateTimePicker from '~/components/DateTimePicker'

function FilterNPSForm({ onClose }) {
  const { query, setQueries } = useSearchParamsHandler()
  const defaultFilters = {
    states: query.states ?? [],
    cities: query.cities ?? [],
    areas: query.areas ?? [],
    labels: query.labels ?? [],
    locations: query.locations ?? [],
    review: query.review ?? [],
    date: query.date ?? [],
  }
  const intl = useIntl()
  const locale = intl.locale === 'en' ? enUS : ptBR
  const dateFormat = intl.locale === 'en' ? 'Y-m-d' : 'd-m-Y'
  const [selectedPeriod, setSelectedPeriod] = useState(
    [
      new Date(query.date[0] + 'T21:00:00-03:00'),
      new Date(query.date[1] + 'T21:00:00-03:00'),
    ] || null
  )
  const [invalidPeriod, setInvalidPeriod] = useState(false)
  const filterCount = getDefaultFiltersCount(defaultFilters)
  const formik = useFormik({
    initialValues: defaultFilters,
    onSubmit: values => {
      setQueries({ ...values, page: 1 })
      toast(intl.formatMessage({ id: 'Filters added successfully' }), {
        position: 'top-center',
        type: 'success',
      })
      onClose()
    },
  })

  const clearFilter = () => {
    setQueries({
      states: [],
      cities: [],
      areas: [],
      labels: [],
      locations: [],
      review: [],
      date: [],
      page: 1,
    })
    onClose()
  }

  const {
    states,
    cities,
    areas,
    labels,
    locations,
    handleFilters,
    filterLoading,
    filters,
  } = useFormFilters({ values: formik.values })

  const setPeriod = dates => {
    if (dates.length !== 2) return

    const [startDate, endDate] = dates

    if (isAfter(startDate, endDate)) {
      setInvalidPeriod(true)
    } else {
      setSelectedPeriod(dates)
      const date = [
        startDate.toISOString().split('T')[0],
        endDate.toISOString().split('T')[0],
      ]
      formik.setFieldValue('date', date)
      setInvalidPeriod(false)
    }
  }

  const placeholder = useMemo(() => {
    if (!selectedPeriod || selectedPeriod.length < 2) {
      return intl.formatMessage({ id: 'Select a date' })
    }
  }, [selectedPeriod])

  return (
    <Wrapper>
      <InputGroup style={{ flexWrap: 'nowrap', alignItems: 'center' }}>
        <InputGroupAddon addonType="append">
          <Calendar size={24} className="mr-1" />
        </InputGroupAddon>
        <DateTimePicker
          id="period-date"
          style={{
            display: 'flex',
            flex: 1,
            minHeight: '40px',
          }}
          value={selectedPeriod}
          onChange={setPeriod}
          invalid={invalidPeriod}
          options={{
            mode: 'range',
            dateFormat,
            locale,
            prevArrow: '',
            nextArrow: '',
          }}
          placeholder={placeholder}
        />
      </InputGroup>
      <Divider />
      <FilterContainer>
        <h3 className="title">
          <FormattedMessage id="Locations" />
        </h3>
        <div className="content__wrapper">
          <MultiSelect
            uniqueKey="stateFilter"
            value={states}
            onChange={v => {
              handleFilters(v, 'state', val =>
                formik.setFieldValue('states', val)
              )
            }}
            placeholder={intl.formatMessage({ id: 'Select a state' })}
            options={filters?.states
              .filter(el =>
                getFilteredStateOptionsWithCityAndArea(el, cities, areas)
              )
              ?.map(state => {
                return {
                  value: [state],
                  label: brazilianStates.find(item => item.uf === state)?.state,
                }
              })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="cityFilter"
            value={cities}
            onChange={v => {
              handleFilters(v, 'city', val =>
                formik.setFieldValue('cities', val)
              )
            }}
            placeholder={intl.formatMessage({ id: 'Select a city' })}
            options={filters?.cities
              ?.filter(item =>
                getFilteredCityOptionsWithStateAndArea(item, states, areas)
              )
              ?.map(city => {
                return {
                  value: [city],
                  label: city,
                }
              })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="areaFilter"
            value={areas}
            onChange={v =>
              handleFilters(v, 'area', val =>
                formik.setFieldValue('areas', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select an area' })}
            options={filters?.areas
              ?.filter(item =>
                getFilteredAreaOptionsWithStateAndCity(item, states, cities)
              )
              ?.map(area => {
                return {
                  value: [area],
                  label: area,
                }
              })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="labelFilter"
            value={labels}
            onChange={v =>
              handleFilters(v, 'label', val =>
                formik.setFieldValue('labels', val)
              )
            }
            placeholder={intl.formatMessage({ id: 'Select a label' })}
            options={filters?.labels?.map(label => {
              return {
                value: [label],
                label: label,
              }
            })}
            loading={filterLoading}
          />
          <MultiSelect
            uniqueKey="locationFilter"
            value={locations}
            onChange={v =>
              handleFilters(v, 'location', val => {
                formik.setFieldValue('locations', val)
              })
            }
            placeholder={intl.formatMessage({ id: 'Select a location' })}
            options={filters?.locations?.map(location => {
              return {
                value: [location.id],
                label: location,
              }
            })}
            renderOption={({ label: location }) => (
              <div className="col">
                <span>{location?.name ?? ''}</span>
                <small style={{ display: 'block', color: '#8D8D8D' }}>
                  {location?.data?.storefrontAddress?.addressLines?.join(
                    ', '
                  ) ?? ''}
                </small>
                <small style={{ display: 'block', color: '#8D8D8D' }}>
                  Store code: {location?.data?.storeCode ?? ''}
                </small>
              </div>
            )}
            loading={filterLoading}
          />
        </div>
      </FilterContainer>
      <Divider />
      <FilterContainer>
        <h3 className="title">
          <FormattedMessage id="Review" />
        </h3>
        <FormGroup check className="form-group">
          <Input
            type="checkbox"
            name="promoters"
            id="promoters"
            className="checkbox"
            checked={formik.values.review?.includes('promoters')}
            onChange={e => {
              const isChecked = e.target.checked
              formik.setFieldValue(
                'review',
                isChecked
                  ? [...formik.values.review, 'promoters']
                  : formik.values.review.filter(item => item !== 'promoters')
              )
            }}
          />
          <Label for="promoters" className="label-checkbox">
            <FormattedMessage id="Promoters" />
          </Label>
        </FormGroup>
        <FormGroup check className="form-group">
          <Input
            type="checkbox"
            name="neutrals"
            id="neutrals"
            className="checkbox"
            checked={formik.values.review?.includes('neutrals')}
            onChange={e => {
              const isChecked = e.target.checked
              formik.setFieldValue(
                'review',
                isChecked
                  ? [...formik.values.review, 'neutrals']
                  : formik.values.review.filter(item => item !== 'neutrals')
              )
            }}
          />
          <Label for="neutrals" className="label-checkbox">
            <FormattedMessage id="Neutrals" />
          </Label>
        </FormGroup>
        <FormGroup check className="form-group">
          <Input
            type="checkbox"
            name="detractors"
            id="detractors"
            className="checkbox"
            checked={formik.values.review?.includes('detractors')}
            onChange={e => {
              const isChecked = e.target.checked
              formik.setFieldValue(
                'review',
                isChecked
                  ? [...formik.values.review, 'detractors']
                  : formik.values.review.filter(item => item !== 'detractors')
              )
            }}
          />
          <Label for="detractors" className="label-checkbox">
            <FormattedMessage id="Detractors" />
          </Label>
        </FormGroup>
      </FilterContainer>
      <div className="modal-footer__variant">
        <Button
          color="primary"
          onClick={() => formik.handleSubmit()}
          style={{ width: '100%' }}
          disabled={!formik.dirty}
        >
          <FormattedMessage id="Add filters" />
        </Button>
        <Button
          color="secondary"
          outline
          onClick={clearFilter}
          style={{ width: '100%' }}
          disabled={filterCount === 0}
        >
          <FormattedMessage id="Clear filter" />
        </Button>
      </div>
    </Wrapper>
  )
}
export default FilterNPSForm
